import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { DefaultRootState } from "react-redux";

import { getAnvilWidgetDOMId } from "layoutSystems/common/utils/LayoutElementPositionsObserver/utils";
// Shared with the palette's "Move widget" command. anvil/common/messages is a
// LEAF (plain message factories, no app-graph imports), so importing it here
// does not put the keyboardDnD cluster on the editor's import cycle.
import { MOVE_WIDGET_LABEL } from "layoutSystems/anvil/common/messages";

import {
  useKeyboardDnDRegistry,
  type KeyboardDnDArenaEntry,
} from "./KeyboardDnDArenaRegistry";
import type { KeyboardDnDHighlight } from "./keyboardDnDTypes";
import {
  cycleCanvas,
  findStartIndex,
  nextHighlightIndex,
} from "./keyboardDnDHelpers";
import {
  ANVIL_KEYBOARD_MOVE_START_EVENT,
  type AnvilKeyboardMoveStartDetail,
} from "./keyboardDnDEvents";
import {
  formatCancelAnnouncement,
  formatCannotMoveAnnouncement,
  formatCanvasDeadEndAnnouncement,
  formatDeadEndAnnouncement,
  formatDropAnnouncement,
  formatKeyboardMoveEndedAnnouncement,
  formatLiftAnnouncement,
  formatMoveAnnouncement,
} from "./keyboardDnDMessages";

/**
 * Keyboard DnD move mode (design v2).
 *
 * Mounted once in AnvilEditorCanvas. Lift arrives via the
 * ANVIL_KEYBOARD_MOVE_START_EVENT custom event (widget name-chip handle /
 * command palette) and is TWO-PHASE: arenas register their droppability
 * verdicts and highlight closures from useAnvilDnDListenerStates, which
 * derives them from the CURRENT drag state — before any drag, allowToDrop is
 * always false and highlight closures capture an empty dragged-blocks list.
 * So phase 1 only resolves the parent arena and dispatches the shared drag
 * state (via the arena's beginDrag); phase 2 (an effect, after the arenas
 * re-register with fresh post-drag verdicts) gates the move and enters the
 * mode — or rolls the drag state back, never leaving an orphaned drag.
 *
 * While active:
 * - real DOM focus sits on this controller (role="listbox"), which flips
 *   screen readers out of browse mode so arrows reach the app;
 * - arrows navigate the arena's insertion highlights linearly (clamped);
 * - Enter/Space commits via the arena's onDrop (mouse-parity guard: the
 *   current-position highlight is a no-op cancel);
 * - Escape cancels — handled capture-phase and swallowed so the global esc
 *   (deselect + close panes) does not also fire (two-stage semantics);
 * - any pointer activity hands the drag over to the mouse flow (mode exits,
 *   drag state stays); external drag deactivation also exits the mode;
 * - every outcome is voiced (never-silent contract), and mode-ending exits
 *   restore focus to where the lift started.
 *
 * The mode holds layoutIds only — arena entries are re-resolved from the
 * registry at every operation, so re-registered arenas (fresh closures over
 * fresh drag state) are always the ones driven. The author-locked mouse path
 * (useAnvilDnDEvents.ts) is untouched.
 *
 * DEPENDENCY DISCIPLINE: this module (and everything under keyboardDnD/)
 * must not import from the app-wide graph (selectors, actions, widget
 * utils, anvilTypes) — the cyclic-dependency CI gate counts every module
 * edge, type-only included, and AnvilEditorCanvas already sits on the
 * editor's import cycle. All redux drag operations reach us as arena entry
 * methods (beginDrag/endDrag/makeActiveCanvas/showHighlight), implemented
 * in AnvilHighlightingCanvas; store reads use inline DefaultRootState
 * selectors.
 */

interface MoveModeState {
  layoutId: string;
  widgetId: string;
  highlights: KeyboardDnDHighlight[];
  index: number;
  widgetName: string;
}

interface PendingLift {
  layoutId: string;
  widgetId: string;
  widgetName: string;
}

/** Debounce for rapid arrowing so role="status" text replacement keeps up. */
export const ANNOUNCE_DEBOUNCE_MS = 150;

const isDraggingSelector = (state: DefaultRootState) =>
  state.ui.widgetDragResize.isDragging;

const canvasWidgetsSelector = (state: DefaultRootState) =>
  state.entities.canvasWidgets;

/** Best-effort focus of a widget's DOM node (fallback when the lift-origin
 * chip remounted). Mirrors AnvilWidgetName/utils getWidgetDOMElement without
 * importing it — that module sits on the editor's import cycle. */
function focusWidgetElement(widgetId: string) {
  const domId = getAnvilWidgetDOMId(widgetId);
  // getElementById does no selector parsing, so a widgetId carrying CSS
  // metacharacters (crafted import) degrades to a no-op instead of throwing.
  const element =
    document.getElementById(domId) ?? document.getElementsByClassName(domId)[0];

  if (element instanceof HTMLElement) element.focus();
}

export function KeyboardMoveController() {
  const registry = useKeyboardDnDRegistry();
  const canvasWidgets = useSelector(canvasWidgetsSelector);
  const isDragging = useSelector(isDraggingSelector);

  const controllerRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<MoveModeState | null>(null);
  const modeRef = useRef<MoveModeState | null>(null);
  const [pendingLift, setPendingLift] = useState<PendingLift | null>(null);
  const pendingLiftRef = useRef<PendingLift | null>(null);
  /** Element focused at lift time — focus returns there on drop/cancel. */
  const liftOriginRef = useRef<HTMLElement | null>(null);
  /** Entry that began the drag — endDrag fallback if the arena re-registers
   * away or unmounts (endDrag does not depend on entry freshness). */
  const liftEntryRef = useRef<KeyboardDnDArenaEntry | null>(null);

  useEffect(
    function syncModeRef() {
      modeRef.current = mode;
    },
    [mode],
  );

  // Pre-mounted status region (live regions added together with their first
  // message often do not announce); debounced so rapid arrowing replaces
  // text instead of queueing.
  const [announcement, setAnnouncement] = useState("");
  const announceTimerRef = useRef<number | undefined>(undefined);
  const announce = useCallback((message: string) => {
    if (announceTimerRef.current !== undefined) {
      window.clearTimeout(announceTimerRef.current);
    }

    announceTimerRef.current = window.setTimeout(() => {
      announceTimerRef.current = undefined;
      setAnnouncement(message);
    }, ANNOUNCE_DEBOUNCE_MS);
  }, []);

  useEffect(function clearAnnounceTimerOnUnmount() {
    return () => {
      if (announceTimerRef.current !== undefined) {
        window.clearTimeout(announceTimerRef.current);
      }
    };
  }, []);

  const resolveEntry = useCallback(
    (layoutId: string): KeyboardDnDArenaEntry | undefined =>
      registry?.getEntry(layoutId),
    [registry],
  );

  const zoneNameOf = useCallback(
    (entry: KeyboardDnDArenaEntry) =>
      canvasWidgets[entry.widgetId]?.widgetName ?? entry.widgetId,
    [canvasWidgets],
  );
  const zoneChildNamesOf = useCallback(
    (entry: KeyboardDnDArenaEntry) =>
      (canvasWidgets[entry.widgetId]?.children ?? []).map(
        (childId: string) => canvasWidgets[childId]?.widgetName ?? childId,
      ),
    [canvasWidgets],
  );

  const showHighlight = useCallback(
    (state: MoveModeState, index: number) => {
      const highlight = state.highlights[index];

      resolveEntry(state.layoutId)?.showHighlight(highlight);
    },
    [resolveEntry],
  );

  const clearVisuals = useCallback(
    (state: MoveModeState) => {
      resolveEntry(state.layoutId)?.showHighlight(null);
    },
    [resolveEntry],
  );

  /** Exits the mode; endDrag=false leaves the drag alive (pointer handover). */
  const exitMode = useCallback(
    (endDrag: boolean) => {
      const state = modeRef.current;

      if (!state) return;

      clearVisuals(state);
      setMode(null);
      modeRef.current = null;

      if (endDrag) {
        const entry =
          resolveEntry(state.layoutId) ?? liftEntryRef.current ?? undefined;

        entry?.endDrag();
        // Focus limbo guard: the hidden listbox held real DOM focus during
        // the mode. Return it to the lift origin (the name chip), or the
        // widget element when the origin remounted. Skipped on pointer
        // handover — a focus() there could scroll mid-drag.
        const origin = liftOriginRef.current;

        if (origin && origin.isConnected) {
          origin.focus();
        } else {
          focusWidgetElement(state.widgetId);
        }
      }

      liftOriginRef.current = null;
      liftEntryRef.current = null;
    },
    [clearVisuals, resolveEntry],
  );

  // Lift phase 1: resolve the widget's parent arena and dispatch the shared
  // drag state via the arena. Verdicts and highlights are NOT read here —
  // they are pre-drag stale (see module doc).
  useEffect(
    function listenForKeyboardMoveStart() {
      const onStart = ((e: CustomEvent<AnvilKeyboardMoveStartDetail>) => {
        if (!registry || modeRef.current || pendingLiftRef.current) return;

        const widgetId = e.detail?.widgetId;

        if (!widgetId) return;

        const widget = canvasWidgets[widgetId];
        const widgetName = widget?.widgetName ?? widgetId;

        const entry = widget
          ? registry
              .getEntries()
              .find((candidate) => candidate.widgetId === widget.parentId)
          : undefined;

        if (!widget || !entry) {
          announce(formatCannotMoveAnnouncement(widgetName));

          return;
        }

        liftOriginRef.current =
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        liftEntryRef.current = entry;

        const pending: PendingLift = {
          layoutId: entry.layoutId,
          widgetId,
          widgetName,
        };

        pendingLiftRef.current = pending;
        setPendingLift(pending);
        entry.beginDrag(widget.type);
      }) as EventListener;

      document.addEventListener(ANVIL_KEYBOARD_MOVE_START_EVENT, onStart);

      return () =>
        document.removeEventListener(ANVIL_KEYBOARD_MOVE_START_EVENT, onStart);
    },
    [registry, canvasWidgets, announce],
  );

  // Lift phase 2: the drag state has landed and the arenas — mounted before
  // this controller, so their effects flushed first this commit — have
  // re-registered with fresh post-drag verdicts and closures. Gate the move
  // and enter the mode, or roll the drag back (never an orphaned drag).
  useEffect(
    function enterModeOnceDragStateSettles() {
      if (!pendingLift || !isDragging || !registry) return;

      pendingLiftRef.current = null;
      setPendingLift(null);

      const entry = registry.getEntry(pendingLift.layoutId);
      const allowed = entry && entry.canActivate && entry.allowToDrop;
      const highlights = allowed ? entry.getCompensatedHighlights() : [];

      if (!entry || highlights.length === 0) {
        (entry ?? liftEntryRef.current)?.endDrag();
        liftEntryRef.current = null;
        announce(formatCannotMoveAnnouncement(pendingLift.widgetName));

        return;
      }

      const index = findStartIndex(highlights);
      const state: MoveModeState = {
        layoutId: entry.layoutId,
        widgetId: pendingLift.widgetId,
        highlights,
        index,
        widgetName: pendingLift.widgetName,
      };

      setMode(state);
      modeRef.current = state;
      showHighlight(state, index);
      announce(formatLiftAnnouncement(state.widgetName, zoneNameOf(entry)));
      // Real DOM focus onto the listbox controller — this is what flips
      // NVDA/JAWS out of browse mode so arrow keys reach us.
      requestAnimationFrame(() => controllerRef.current?.focus());
      controllerRef.current?.focus();
    },
    [pendingLift, isDragging, registry, showHighlight, announce, zoneNameOf],
  );

  // Key handling — capture phase so Escape never reaches the global esc
  // hotkey (deselect + close panes) while the mode is active.
  useEffect(
    function attachMoveModeListeners() {
      if (!mode) return;

      const onKeyDown = (e: KeyboardEvent) => {
        const state = modeRef.current;

        if (!state) return;

        switch (e.key) {
          case "ArrowDown":
          case "ArrowRight":
          case "ArrowUp":
          case "ArrowLeft": {
            e.preventDefault();
            e.stopImmediatePropagation();
            const entry = resolveEntry(state.layoutId);

            if (!entry) {
              // The arena unmounted beneath us — end the move.
              announce(formatCancelAnnouncement(state.widgetName));
              exitMode(true);
              break;
            }

            const direction =
              e.key === "ArrowDown" || e.key === "ArrowRight" ? 1 : -1;
            const step = nextHighlightIndex(
              state.index,
              state.highlights.length,
              direction,
            );

            if (step.moved) {
              const next = { ...state, index: step.index };

              setMode(next);
              modeRef.current = next;
              showHighlight(next, step.index);
              announce(
                formatMoveAnnouncement(
                  next.highlights[step.index],
                  step.index,
                  next.highlights.length,
                  zoneNameOf(entry),
                  zoneChildNamesOf(entry),
                  state.widgetName,
                ),
              );
            } else {
              // Clamped: voiced, never silent, never wrapping.
              announce(formatDeadEndAnnouncement(direction));
            }

            break;
          }
          case "Enter":
          case " ": {
            e.preventDefault();
            e.stopImmediatePropagation();
            const entry = resolveEntry(state.layoutId);
            const highlight = state.highlights[state.index];

            // Mouse-parity guard: dropping on the current position is a no-op.
            if (
              entry &&
              highlight &&
              !highlight.existingPositionHighlight &&
              entry.allowToDrop
            ) {
              entry.onDrop(highlight);
              announce(
                formatDropAnnouncement(state.widgetName, zoneNameOf(entry)),
              );
            } else {
              announce(formatCancelAnnouncement(state.widgetName));
            }

            exitMode(true);
            break;
          }
          case "Escape": {
            e.preventDefault();
            e.stopImmediatePropagation();
            announce(formatCancelAnnouncement(state.widgetName));
            exitMode(true);
            break;
          }
          case "Tab":
          case "[":
          case "]": {
            // Cycle droppable canvases; captured only while the mode is live
            // (APG-sanctioned modal interaction; [ and ] are synonyms for
            // screen-reader users who expect Tab to escape).
            e.preventDefault();
            e.stopImmediatePropagation();

            if (!registry) break;

            const direction: 1 | -1 =
              e.key === "[" || (e.key === "Tab" && e.shiftKey) ? -1 : 1;
            const nextEntry = cycleCanvas(
              registry.getEntries(),
              state.layoutId,
              direction,
            );

            if (!nextEntry) {
              // No other droppable canvas — selection unchanged. Never silent.
              announce(formatCanvasDeadEndAnnouncement());
              break;
            }

            const highlights = nextEntry.getCompensatedHighlights();

            if (highlights.length === 0) {
              // Target zone has no insertion positions — voiced, not silent.
              announce(formatCanvasDeadEndAnnouncement());
              break;
            }

            clearVisuals(state);
            nextEntry.makeActiveCanvas();

            const index = findStartIndex(highlights);
            const next: MoveModeState = {
              layoutId: nextEntry.layoutId,
              widgetId: state.widgetId,
              highlights,
              index,
              widgetName: state.widgetName,
            };

            setMode(next);
            modeRef.current = next;
            showHighlight(next, index);
            announce(
              formatMoveAnnouncement(
                highlights[index],
                index,
                highlights.length,
                zoneNameOf(nextEntry),
                zoneChildNamesOf(nextEntry),
                state.widgetName,
              ),
            );
            break;
          }
          default:
            break;
        }
      };

      // Pointer handover: any mouse movement hands the drag to the pointer
      // flow — the shared drag state keeps it alive; only the keyboard mode
      // exits. Voiced so a stray trackpad brush is never a silent dead end.
      const onMouseMove = () => {
        announce(formatKeyboardMoveEndedAnnouncement());
        exitMode(false);
      };

      window.addEventListener("keydown", onKeyDown, true);
      window.addEventListener("mousemove", onMouseMove, true);

      return () => {
        window.removeEventListener("keydown", onKeyDown, true);
        window.removeEventListener("mousemove", onMouseMove, true);
      };
    },
    [
      mode,
      exitMode,
      showHighlight,
      clearVisuals,
      registry,
      resolveEntry,
      announce,
      zoneNameOf,
      zoneChildNamesOf,
    ],
  );

  // External deactivation (stray click, another modality ending the drag):
  // clean up the mode when the shared drag state turns off beneath us.
  useEffect(
    function exitOnExternalDragDeactivation() {
      if (mode && !isDragging) {
        announce(formatKeyboardMoveEndedAnnouncement());
        exitMode(false);
      }

      // A pending lift whose drag was killed before phase 2 consumed it must
      // not wedge future lifts.
      if (!isDragging && pendingLiftRef.current) {
        pendingLiftRef.current = null;
        setPendingLift(null);
      }
    },
    [isDragging, mode, exitMode, announce],
  );

  return (
    <>
      <div
        aria-live="polite"
        role="status"
        style={{
          position: "fixed",
          width: 1,
          height: 1,
          margin: -1,
          padding: 0,
          overflow: "hidden",
          clipPath: "inset(50%)",
          whiteSpace: "nowrap",
        }}
      >
        {announcement}
      </div>
      <div
        aria-activedescendant={
          mode ? `anvil-kb-move-position-${mode.index}` : undefined
        }
        aria-label={MOVE_WIDGET_LABEL()}
        ref={controllerRef}
        role="listbox"
        style={{
          position: "fixed",
          width: 1,
          height: 1,
          margin: -1,
          padding: 0,
          overflow: "hidden",
          clipPath: "inset(50%)",
          whiteSpace: "nowrap",
        }}
        tabIndex={-1}
      >
        {mode?.highlights.map((_, index) => (
          <div
            aria-selected={index === mode.index}
            id={`anvil-kb-move-position-${index}`}
            key={index}
            role="option"
          >
            {`Position ${index + 1} of ${mode.highlights.length}`}
          </div>
        ))}
      </div>
    </>
  );
}
